import { Outlet, createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { authApi } from "../features/auth/api.ts";
import { BlockedScreen, PendingApprovalScreen } from "../features/auth/components/AccessGateScreens.tsx";
import { useAuthStore } from "../features/auth/store.ts";
import { companyApi } from "../features/company/api.ts";
import { getTenantSubdomain } from "../lib/subdomain.ts";
import { AppFooter } from "../features/dashboard/components/AppFooter.tsx";
import { OnboardingAgentWidget } from "../features/onboarding-agent/OnboardingAgentWidget.tsx";
import { OnboardingVideoModal } from "../features/onboarding/OnboardingVideoModal.tsx";
import { WhatsNewModal } from "../features/whatsNew/WhatsNewModal.tsx";
import { ApiError } from "../lib/http.ts";

/**
 * Layout route that requires:
 *   1. User is signed in (firebaseUid set)
 *   2. Gemini API key is configured (geminiKey in localStorage)
 *
 * IMPORTANT: a failed `/users/me` fetch does NOT log the user out.
 * Network errors / 5xx / unreachable api just leave `user=null` and surface
 * a non-blocking banner. We only force-signout on a real 401 from the api,
 * because that means our auth credential is genuinely invalid.
 */
export const Route = createFileRoute("/_app")({
  component: AppShell,
});

function AppShell() {
  const nav = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const initialized = useAuthStore((s) => s.initialized);
  const firebaseUid = useAuthStore((s) => s.firebaseUid);
  const clear = useAuthStore((s) => s.clear);
  const onboardingVideoSeen = useAuthStore((s) => s.onboardingVideoSeen);
  const setOnboardingVideoSeen = useAuthStore((s) => s.setOnboardingVideoSeen);
  const geminiKey = useAuthStore((s) => s.geminiKey);
  const setGeminiKey = useAuthStore((s) => s.setGeminiKey);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const [profileError, setProfileError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Resolve active company workspace roles from subdomain
  useEffect(() => {
    const tenantSub = getTenantSubdomain();
    if (tenantSub) {
      companyApi.resolveSubdomain(tenantSub).then((info) => {
        if (info?.roles && info.roles.length > 0) {
          useAuthStore.getState().setCompanyRoles(info.roles);
        }
      }).catch(() => {});
    }
  }, []);

  // Sign-in gate — only redirects when we're sure the user is not signed in.
  useEffect(() => {
    if (!initialized) return;
    if (!firebaseUid) {
      nav({ to: "/signin" });
    }
  }, [initialized, firebaseUid, nav]);

  // Fetch /users/me once signed-in. Do NOT redirect to /signin on failure
  // unless the api specifically rejects the credential with 401.
  useEffect(() => {
    if (!firebaseUid || user) return;
    authApi
      .fetchMe()
      .then((u) => {
        setUser(u);
        setProfileError(null);
        if (u.companyRoles && u.companyRoles.length > 0) {
          useAuthStore.getState().setCompanyRoles(u.companyRoles);
        }
        // Hydrate the key the user saved on their account (remembered across
        // devices) if this browser doesn't already have one.
        if (u.geminiKey && !useAuthStore.getState().geminiKey) setGeminiKey(u.geminiKey);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          clear();
          nav({ to: "/signin" });
          return;
        }
        // Transient: network error / 5xx / api unreachable. Stay signed-in,
        // surface a banner. The user can still navigate around.
        const msg = err instanceof Error ? err.message : "Profile fetch failed";
        setProfileError(msg);
      });
  }, [firebaseUid, user, setUser, setGeminiKey, clear, nav]);

  // Auto-provision the GCP account's Gemini key for all users on first load
  // so users are never forced to configure their own API key.
  useEffect(() => {
    if (!initialized || !firebaseUid || geminiKey) return;
    let cancelled = false;
    authApi
      .getManagedKey()
      .then((r) => {
        if (!cancelled && r?.key) setGeminiKey(r.key);
      })
      .catch((err) => {
        console.warn("Auto-provision GCP key error (using fallback):", err);
      });
    return () => {
      cancelled = true;
    };
  }, [initialized, firebaseUid, geminiKey, setGeminiKey]);

  // First login → suggest the onboarding video once. The flag is persisted, so
  // it won't pop again on later sessions; the header (?) icon reopens it anytime.
  useEffect(() => {
    if (initialized && firebaseUid && !onboardingVideoSeen && pathname !== "/company") {
      setShowOnboarding(true);
    }
  }, [initialized, firebaseUid, onboardingVideoSeen]);

  function dismissOnboarding() {
    setShowOnboarding(false);
    setOnboardingVideoSeen(true);
  }

  // Access-control gate: a pending/blocked user sees the corresponding screen
  // instead of the app (server-side enforcement in the auth hook backs this up).
  if (user?.status === "pending") return <PendingApprovalScreen />;
  if (user?.status === "blocked") return <BlockedScreen />;

  return (
    <>
      {profileError && (
        <div
          style={{
            background: "var(--gc-yellow-50)",
            color: "#7A4C00",
            padding: "8px 16px",
            fontSize: 13,
            borderBottom: "1px solid var(--border-soft)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            Couldn't reach the profile service: {profileError}. You stay signed in; some features
            may be limited.
          </span>
          <button
            type="button"
            onClick={() => setProfileError(null)}
            style={{ background: "transparent", border: 0, cursor: "pointer", color: "inherit" }}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
      <Outlet />
      <AppFooter />
      <OnboardingVideoModal open={showOnboarding} onClose={dismissOnboarding} />
      <WhatsNewModal />
      {user && user.settings?.onboardingAgentEnabled !== false && pathname !== "/apikey" && (
        <OnboardingAgentWidget />
      )}
    </>
  );
}
